import { Outlet } from 'react-router-dom'
import { Header } from './Header'
import { Footer } from './Footer'
import { EnvironmentBanner } from './EnvironmentBanner'
import { useUIStore } from '@/store/uiStore'
import { useLanguageSync } from '@/hooks/useLanguageSync'
import { cn } from '@/lib/utils'
import { DashboardMapAnchorProvider } from '@/contexts/DashboardMapAnchorContext'
import { PersistentDashboardMap } from '@/components/maps/PersistentDashboardMap'
import { DirectionalSidebar } from './variants/DirectionalShell'
import { useShellLayout } from './variants/useShellLayout'
import { CommandOpsStrip } from './variants/CommandOpsStrip'
import { CommandPalette } from '@/components/command/CommandPalette'

export function MainLayout() {
  const { sidebarCollapsed, sidebarOpen, setSidebarOpen } = useUIStore()
  useLanguageSync()
  const shell = useShellLayout()

  return (
    <DashboardMapAnchorProvider>
      {/* PersistentDashboardMap renders once per session — outside <Outlet> so
          it never unmounts on route changes. It renders either null or a React
          Portal into the anchor div that DashboardPage publishes. */}
      <PersistentDashboardMap />

      {/* CommandPalette: always mounted, internally inert unless direction==='saas'.
          Keyboard listener only attaches when saas is active. */}
      <CommandPalette />

      <div className="relative flex h-[100dvh] w-full bg-background overflow-hidden">
        {/* Mobile overlay — fade transition */}
        <div
          className={cn(
            'fixed inset-0 z-40 bg-black/50 lg:hidden transition-opacity duration-300',
            sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          )}
          onClick={() => setSidebarOpen(false)}
        />

        <DirectionalSidebar />

        {/* Main content wrapper — fixed height so header sticks and only main scrolls */}
        <div
          className={shell.ml ? cn(
            'flex flex-1 flex-col h-full w-full overflow-hidden',
            shell.ml,
            shell.canvas && 'bg-canvas'
          ) : cn(
            'flex flex-1 flex-col h-full w-full overflow-hidden',
            // Desktop: offset by sidebar width, synced transition with sidebar
            'lg:ml-64 lg:transition-[margin-left] lg:duration-300',
            sidebarCollapsed && 'lg:ml-16'
          )}
        >
          <EnvironmentBanner />
          {shell.opsStrip && <CommandOpsStrip />}
          {/* When the ops strip is rendered it occupies h-8 above the Header.
              We push Header's own sticky top down by 8 via a wrapper class so
              it stays pinned directly below the strip — Header.tsx is untouched. */}
          {shell.opsStrip ? <div className="[&>header]:top-8"><Header /></div> : <Header />}

          {/* Main content area - only this scrolls */}
          <main className="flex-1 overflow-x-hidden overflow-y-auto flex flex-col min-h-0" style={{ willChange: 'transform' }}>
            <div className="p-3 sm:p-4 md:p-6 lg:p-8 max-w-full min-w-0 flex-1">
              <Outlet />
            </div>
            <Footer />
          </main>
        </div>
      </div>
    </DashboardMapAnchorProvider>
  )
}
